import { AppShell, NavLinkInner, ThemeMenuRow, navLinkClass } from "@/components/app-shell";
import { cn } from "@/lib/utils";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, FolderSearch, ShieldCheck, Users } from "lucide-react";

const ADMIN_NAV = [
  { to: "/admin/searches", label: "Searches", icon: FolderSearch },
  { to: "/admin/candidates", label: "Candidates", icon: Users },
  { to: "/admin/users", label: "Users", icon: ShieldCheck },
] as const;

/** Admin brand lockup — same mark as the client, distinguished by the "Admin" eyebrow so the
 *  back-office reads as the internal face of one product. */
function AdminWordmark() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="inline-block size-3 rounded-sm bg-[var(--brand-accent)]" />
      <div className="leading-none">
        <div className="text-sm font-semibold tracking-tight">OW × Canvas</div>
        <div className="eyebrow mt-0.5 text-[10px]">Admin</div>
      </div>
    </div>
  );
}

function AdminNav({ collapsed }: { collapsed: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="space-y-1">
      {ADMIN_NAV.map((item) => {
        const active = pathname.startsWith(item.to);
        return (
          <Link
            key={item.to}
            to={item.to}
            title={collapsed ? item.label : undefined}
            className={navLinkClass(active, collapsed)}
          >
            <NavLinkInner icon={item.icon} label={item.label} collapsed={collapsed} active={active} />
          </Link>
        );
      })}
    </div>
  );
}

/** Admin footer: a link back to the client portal, set off by a hairline from the appearance toggle —
 *  the same lightweight menu-row shape the client footer uses. */
function AdminFooter() {
  return (
    <div className="space-y-0.5">
      <Link to="/" className={navLinkClass(false, false)}>
        <ArrowLeft className="size-4 shrink-0" />
        <span className="flex-1 truncate">Client portal</span>
      </Link>
      <div className="-mx-2 border-t px-2 pt-1">
        <ThemeMenuRow />
      </div>
    </div>
  );
}

/** Admin mobile bar — the same three destinations as a horizontal strip below `md`. */
function AdminMobileTopbar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur md:hidden">
      <div className="flex h-14 items-center gap-4 px-4">
        <AdminWordmark />
        <nav className="flex items-center gap-1 overflow-x-auto">
          {ADMIN_NAV.map((item) => {
            const active = pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
                  active ? "font-medium text-foreground" : "text-muted-foreground",
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <Link to="/" className="ml-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Portal
        </Link>
      </div>
    </header>
  );
}

/** Internal back-office shell — now the same left-sidebar frame as the client portal (audit P8), so
 *  the two areas read as one product rather than two. Content keeps its focused reading width. */
export function AdminShell() {
  return (
    <AppShell
      brand={<AdminWordmark />}
      nav={(collapsed) => <AdminNav collapsed={collapsed} />}
      footer={() => <AdminFooter />}
      mobile={<AdminMobileTopbar />}
      mainClassName="mx-auto max-w-[1100px] px-6 py-8 md:px-10"
    >
      <Outlet />
    </AppShell>
  );
}
